import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { creditsToQuantity, getPaymentFailurePresentation, getUserBillingPage, PaymentFailureModal } from '@wegic/price-ui';
import { Loading, Message, Modal } from '@wegic/ui';
import '@wegic/ui/dist/components/Loading/index.css';
import '@wegic/ui/dist/components/Message/index.css';
import '@wegic/ui/dist/components/Modal/index.css';
import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getRemixCreditPurchaseTrackingContext } from '@/components/GlobalCreditPurchaseModal/tracking';
import StripeCheckoutDrawer from '@/components/StripeCheckoutDrawer';
import { collectUpdatePaymentMethodCta } from '@/helpers/payment/updatePaymentMethodTracking';
import { createEmbeddedCheckoutOptions, createPaymentSuccessWaiter, waitForPaymentSuccessOrRefresh } from '@/helpers/paymentCheckout';
import { postSubscriptionRequest } from '@/services/user';
import { useI18nStore } from '@/stores/i18n';
import { useRemixUpgradeModalStore } from '@/stores/remixUpgradeModal';
import { CREDIT_PURCHASE_LIMIT_EXCEEDED_CODE } from './const';
import CreditsTab from './CreditsTab';
import styles from './index.module.less';
import SubscriptionTab from './SubscriptionTab';
function getRemixPlanModelTabType(tab) {
    return tab === 'subscription' ? 'plan' : 'credits';
}
function collectRemixCreditCheckoutOpen() {
    collect('checkout_open', {
        ...getRemixCreditPurchaseTrackingContext(),
        checkout_type: 'credits'
    });
}
function startRemixCreditPurchase(payload, language) {
    return postSubscriptionRequest({
        lineItem: {
            productId: payload.productId,
            priceId: payload.priceId,
            quantity: creditsToQuantity(payload.credits)
        },
        source: 'remix',
        successUrl: window.location.href,
        cancelUrl: window.location.href,
        redirectOnCompletion: 'NEVER',
        language
    });
}
export default function RemixUpgradeModal() {
    const visible = useRemixUpgradeModalStore((s)=>s.visible);
    const close = useRemixUpgradeModalStore((s)=>s.close);
    const mode = useRemixUpgradeModalStore((s)=>s.mode);
    const quota = useRemixUpgradeModalStore((s)=>s.quota);
    const loading = useRemixUpgradeModalStore((s)=>s.loading);
    const productInfo = useRemixUpgradeModalStore((s)=>s.productInfo);
    const refresh = useRemixUpgradeModalStore((s)=>s.refresh);
    const openOptions = useRemixUpgradeModalStore((s)=>s.openOptions);
    const currentLanguage = useI18nStore((s)=>s.currentLanguage);
    const [isStartingCheckout, setIsStartingCheckout] = useState(false);
    const [checkoutDrawerVisible, setCheckoutDrawerVisible] = useState(false);
    const [stripeOptions, setStripeOptions] = useState(null);
    const [isWaitingForPaymentSocket, setIsWaitingForPaymentSocket] = useState(false);
    const [paymentFailure, setPaymentFailure] = useState(null);
    const hasCollectedOpenRef = useRef(false);
    const finishPayment = useCallback(async function() {
        let waiter = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : createPaymentSuccessWaiter();
        setIsWaitingForPaymentSocket(true);
        try {
            await waitForPaymentSuccessOrRefresh(waiter);
            await useRemixUpgradeModalStore.getState().refresh();
            Message.success(intl.get('remixUpgradeModal.purchaseSuccess'));
            collect('pay_success', getRemixCreditPurchaseTrackingContext());
            setCheckoutDrawerVisible(false);
            setStripeOptions(null);
        } finally{
            setIsWaitingForPaymentSocket(false);
        }
    }, []);
    const handleBuy = useCallback(async (payload)=>{
        if (isStartingCheckout) {
            return;
        }
        setPaymentFailure(null);
        setIsStartingCheckout(true);
        const autoPayWaiter = createPaymentSuccessWaiter();
        try {
            var _res_data, _res_data1;
            const res = await startRemixCreditPurchase(payload, currentLanguage);
            if (res.code === 0 && ((_res_data = res.data) === null || _res_data === void 0 ? void 0 : _res_data.autoPay) === true) {
                collectRemixCreditCheckoutOpen();
                close();
                setCheckoutDrawerVisible(true);
                await finishPayment(autoPayWaiter);
                return;
            }
            autoPayWaiter.cancel();
            if (res.code === 0 && ((_res_data1 = res.data) === null || _res_data1 === void 0 ? void 0 : _res_data1.clientSecret)) {
                const options = createEmbeddedCheckoutOptions(res.data.clientSecret, async ()=>{
                    setStripeOptions(null);
                    await finishPayment();
                });
                setStripeOptions(options);
                collectRemixCreditCheckoutOpen();
                close();
                setCheckoutDrawerVisible(true);
                return;
            }
            if (res.code === CREDIT_PURCHASE_LIMIT_EXCEEDED_CODE) {
                Message.error(intl.get('remixUpgradeModal.limitExceeded'));
                await refresh();
                return;
            }
            const failure = getPaymentFailurePresentation(res);
            if (failure.checkoutDisplay === 'tip') {
                Message.error(intl.get(failure.messageKey));
            } else {
                setPaymentFailure(failure);
                collectUpdatePaymentMethodCta('show', 'credits_checkout');
            }
        } catch (error) {
            autoPayWaiter.cancel();
            console.error('remix credit checkout failed:', error);
            const failure = getPaymentFailurePresentation(error);
            if (failure.checkoutDisplay === 'tip') {
                Message.error(intl.get(failure.messageKey));
            } else {
                setPaymentFailure(failure);
                collectUpdatePaymentMethodCta('show', 'credits_checkout');
            }
        } finally{
            setIsStartingCheckout(false);
        }
    }, [
        close,
        currentLanguage,
        finishPayment,
        isStartingCheckout,
        refresh
    ]);
    const handleCheckoutCancel = useCallback(()=>{
        if (isWaitingForPaymentSocket) {
            return;
        }
        setCheckoutDrawerVisible(false);
        setStripeOptions(null);
    }, [
        isWaitingForPaymentSocket
    ]);
    // 数据尚未就绪：接口还在请求中，或首次打开时数据还没到
    const isDataReady = !loading && quota !== null && productInfo !== null;
    // 积分配额已耗尽（已购满 300）
    const isCreditsExhausted = quota !== null && quota.remaining <= 0;
    const hasPurchasedCredits = quota !== null && quota.purchased > 0;
    // Tab 的展示判断逻辑
    const canShowCreditsTab = openOptions.visibleTabs.includes('credits') && !isCreditsExhausted;
    const canShowSubscriptionTab = openOptions.visibleTabs.includes('subscription');
    const showSwitch = canShowCreditsTab && canShowSubscriptionTab;
    const [tab, setTab] = useState(mode === 'subscription' ? 'subscription' : 'credits');
    const headerTitle = tab === 'subscription' ? intl.get('remixUpgradeModal.subscriptionTitle') : hasPurchasedCredits ? intl.get('remixUpgradeModal.editingCreditsTitle') : intl.get('remixUpgradeModal.title');
    const headerSubtitle = tab === 'subscription' ? '' : intl.get('remixUpgradeModal.subtitle');
    useEffect(()=>{
        if (!visible) {
            setPaymentFailure(null);
        }
    }, [
        visible
    ]);
    useEffect(()=>{
        if (mode === 'credits' && canShowCreditsTab) {
            setTab('credits');
            return;
        }
        if (canShowSubscriptionTab) {
            setTab('subscription');
            return;
        }
        if (canShowCreditsTab) {
            setTab('credits');
        }
    }, [
        canShowCreditsTab,
        canShowSubscriptionTab,
        mode
    ]);
    useEffect(()=>{
        if (!visible) {
            hasCollectedOpenRef.current = false;
            return;
        }
        if (!isDataReady || hasCollectedOpenRef.current) {
            return;
        }
        hasCollectedOpenRef.current = true;
        collect('remix_plan_model');
    }, [
        isDataReady,
        visible
    ]);
    const handleTabClick = useCallback((nextTab)=>{
        if (nextTab === tab) {
            return;
        }
        setTab(nextTab);
        collect('remix_plan_model_tab', {
            type: getRemixPlanModelTabType(nextTab)
        });
    }, [
        tab
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Modal, {
                className: classNames(styles['remix-upgrade-modal'], {
                    [styles['remix-upgrade-modal--subscription']]: isDataReady && tab === 'subscription'
                }),
                containerClassName: styles['remix-upgrade-modal__container'],
                closeClassName: styles['remix-upgrade-modal__close'],
                visible: visible,
                onCancel: close,
                hideFooter: true,
                unmountOnExit: true,
                children: !isDataReady ? /*#__PURE__*/ _jsx("div", {
                    className: styles['remix-upgrade-modal__loading'],
                    children: /*#__PURE__*/ _jsx(Loading, {})
                }) : /*#__PURE__*/ _jsxs("div", {
                    className: classNames(styles['remix-upgrade-modal__inner'], {
                        [styles['remix-upgrade-modal__inner--subscription']]: tab === 'subscription'
                    }),
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.header,
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: styles.header__titles,
                                    children: [
                                        /*#__PURE__*/ _jsx("h2", {
                                            className: styles.header__title,
                                            children: headerTitle
                                        }),
                                        headerSubtitle && /*#__PURE__*/ _jsx("p", {
                                            className: styles.header__subtitle,
                                            children: headerSubtitle
                                        })
                                    ]
                                }),
                                showSwitch && /*#__PURE__*/ _jsxs("div", {
                                    className: classNames(styles.switch),
                                    children: [
                                        /*#__PURE__*/ _jsx("button", {
                                            type: "button",
                                            className: classNames(styles.switch__item, {
                                                [styles['switch__item--active']]: tab === 'credits'
                                            }),
                                            onClick: ()=>handleTabClick('credits'),
                                            children: intl.get('remixUpgradeModal.tab.credits')
                                        }),
                                        /*#__PURE__*/ _jsx("button", {
                                            type: "button",
                                            className: `${styles.switch__item} ${tab === 'subscription' ? styles['switch__item--active'] : ''}`,
                                            onClick: ()=>handleTabClick('subscription'),
                                            children: intl.get('remixUpgradeModal.tab.subscription')
                                        })
                                    ]
                                })
                            ]
                        }),
                        tab === 'credits' ? /*#__PURE__*/ _jsx(CreditsTab, {
                            buying: isStartingCheckout,
                            onBuy: handleBuy
                        }) : /*#__PURE__*/ _jsx(SubscriptionTab, {})
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(StripeCheckoutDrawer, {
                visible: checkoutDrawerVisible,
                stripeOptions: stripeOptions,
                isWaitingForPayment: isWaitingForPaymentSocket,
                onCancel: handleCheckoutCancel,
                waitingText: intl.get('remixUpgradeModal.processingPayment'),
                onRetry: ()=>collect('stripe_checkout_retry', {
                        checkout_type: 'credits'
                    }),
                onRetryShow: (params)=>collect('stripe_checkout_retry_show', {
                        ...params,
                        checkout_type: 'credits'
                    })
            }),
            paymentFailure && /*#__PURE__*/ _jsx(PaymentFailureModal, {
                visible: true,
                failure: paymentFailure,
                onCancel: ()=>setPaymentFailure(null),
                onUpdatePaymentMethod: ()=>{
                    collectUpdatePaymentMethodCta('click', 'credits_checkout');
                    getUserBillingPage();
                }
            })
        ]
    });
}
